// Single source of truth for "is this member online?" decisions.
//
// Every component that paints a presence dot, a "Online now" badge, or
// any other live-presence UI MUST import this — never inline the rule.
// Two callers with slightly different copies of this logic is exactly
// what produced "Online now" text next to an offline dot on the
// workspace members page.

// Window used by the recency fallback. Matches the server's heartbeat
// timeout (2 missed heartbeats at 5s each = 10s) plus a small grace
// buffer for socket reconnects. Anything longer just keeps offline
// users falsely green.
export const ONLINE_ACTIVE_WINDOW_MS = 30 * 1000;

/**
 * Decide whether a member should be shown as online.
 *
 * Priority:
 *  0. Self — if `currentUserId` matches `member._id`, the answer is
 *     always `true`. We're rendering this from a logged-in session;
 *     the user obviously cannot be offline in an app they're using.
 *     This bypasses any socket/recency race on fresh page loads.
 *  1. Live socket map (OnlineStatusContext) — authoritative once a
 *     snapshot has arrived.
 *  2. Recency of `member.lastActive` (within ONLINE_ACTIVE_WINDOW_MS) —
 *     bridges the small window between mount and the first snapshot,
 *     and covers the case where a user has activity but no live socket
 *     (e.g. background tab).
 *  3. `member.online === true` (DB-persisted) — only when we have no
 *     `lastActive` at all. This is the weakest signal because the DB
 *     flag can lag a hard disconnect; we only use it as a last resort.
 */
export function isMemberOnline(member, onlineUsers, currentUserId) {
    if (!member?._id) return false;

    if (currentUserId && String(member._id) === String(currentUserId)) return true;

    if (onlineUsers && onlineUsers[member._id] === true) return true;

    if (member.lastActive) {
        const lastActiveTs = new Date(member.lastActive).getTime();
        if (!Number.isNaN(lastActiveTs) && Date.now() - lastActiveTs < ONLINE_ACTIVE_WINDOW_MS) {
            return true;
        }
        return false;
    }

    return member.online === true;
}
